import React, { useEffect } from "react"
import { Link, Outlet, useNavigate, useRouterState } from "@tanstack/react-router"
import { useAuth } from "@/contexts/AuthContext"
import { useDocuments, useMyAssignments } from "@/lib/queries"
import { Button } from "@/components/ui/button"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog"
import { LogOut, Settings } from "lucide-react"
import { Footer } from "@/components/Footer"

const allNavItems = [
  { label: "Home", to: "/", documentType: null },
  { label: "Clinical Reviews", to: "/clinical-reviews", documentType: "clinical_review" },
  { label: "Workflows", to: "/workflows", documentType: "workflow_template" },
  { label: "Supplemental", to: "/supplemental", documentType: "supplemental" },
]

export function CustomerLayout() {
  const { user, isAuthenticated, isLoading, logout, requestPasswordReset } =
    useAuth()
  const navigate = useNavigate()
  const routerState = useRouterState()
  const currentPath = routerState.location.pathname
  const { data: documentGroups, isLoading: docsLoading } = useDocuments()
  // Shares the query key the training page already uses, so this costs no
  // extra request once either has loaded.
  const quizzesEnabled = user?.quizzes_enabled === true
  const canTakeTraining =
    quizzesEnabled && (user?.role === "staff" || user?.role === "manager")
  const { data: myAssignments } = useMyAssignments({ enabled: canTakeTraining })
  // Everything still requiring action: never opened, part-finished, or failed
  // and awaiting a retake. A failed assessment is "submitted" but it is not
  // done with the person, so counting it as complete would hide the one row
  // they most need to come back to.
  const outstandingCount = (myAssignments ?? []).filter(
    (a) => a.closed_at === null && a.status !== "passed"
  ).length

  // Filter nav items to only show types that have documents
  const availableTypes = new Set(
    (documentGroups ?? [])
      .filter((g) => g.documents.length > 0)
      .map((g) => g.document_type)
  )
  // Show all items while loading to prevent flash, then filter
  const navItems = docsLoading
    ? allNavItems
    : allNavItems.filter(
        (item) => item.documentType === null || availableTypes.has(item.documentType)
      )

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      const currentUrl = window.location.pathname + window.location.search + window.location.hash
      navigate({ to: "/login", search: { redirect: currentUrl } })
    }
  }, [isLoading, isAuthenticated, navigate])

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <p className="text-muted-foreground">Loading...</p>
      </div>
    )
  }

  if (!isAuthenticated) {
    return null
  }

  return (
    <div className="flex h-screen flex-col">
      {/* Top navigation bar */}
      <header className="flex h-14 shrink-0 items-center border-b bg-background px-6">
        {/* Logo */}
        <Link to="/" className="mr-8 flex items-center">
          <img
            src="/signature_logo.png"
            alt="Specia"
            className="h-7 w-auto object-contain"
          />
        </Link>

        {/* Nav links */}
        <nav className="flex items-center gap-1">
          {navItems.map((item) => {
            const isActive =
              item.to === "/"
                ? currentPath === "/"
                : currentPath.startsWith(item.to)
            return (
              <Link
                key={item.to}
                to={item.to}
                className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                  isActive
                    ? "bg-accent text-foreground"
                    : "text-muted-foreground hover:bg-accent hover:text-foreground"
                }`}
              >
                {item.label}
              </Link>
            )
          })}
          {/* Managers see this too: one may assign an assessment to themselves.
              Not conditional on having any — an empty page is honest, a nav
              item that appears and disappears with the data is not. */}
          {canTakeTraining && (
            <Link
              to="/training"
              className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                currentPath.startsWith("/training")
                  ? "bg-accent text-foreground"
                  : "text-muted-foreground hover:bg-accent hover:text-foreground"
              }`}
            >
              My Training
              {outstandingCount > 0 && (
                <span
                  className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-amber-500 px-1.5 text-xs font-semibold text-white"
                  aria-label={`${outstandingCount} assessment${outstandingCount === 1 ? "" : "s"} outstanding`}
                >
                  {outstandingCount}
                </span>
              )}
            </Link>
          )}
          {quizzesEnabled && user?.role === "manager" && (
            <>
              {/* Divides the organization administration items from the ones
                  every user has. Decorative, so hidden from assistive tech —
                  and rendered only for managers, who are the only people with
                  anything on the other side of it. */}
              <span aria-hidden="true" className="mx-2 h-5 w-px bg-border" />
              <Link
                to="/manage/assignments"
                className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                  currentPath.startsWith("/manage/assignments")
                    ? "bg-accent text-foreground"
                    : "text-muted-foreground hover:bg-accent hover:text-foreground"
                }`}
              >
                Training Admin
              </Link>
            </>
          )}
        </nav>

        {/* Right side: org name + logout */}
        <div className="ml-auto flex items-center gap-3">
          <span className="text-sm text-muted-foreground">
            {user?.organization_name}
          </span>
          {user?.role === "manager" && (
            <Link
              to="/settings"
              title="Settings"
              className={`inline-flex h-9 w-9 items-center justify-center rounded-md transition-colors ${
                currentPath.startsWith("/settings")
                  ? "bg-accent text-foreground"
                  : "text-muted-foreground hover:bg-accent hover:text-foreground"
              }`}
            >
              <Settings className="h-4 w-4" />
              <span className="sr-only">Settings</span>
            </Link>
          )}
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="ghost" size="icon" title="Logout">
                <LogOut className="h-4 w-4" />
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Log out</AlertDialogTitle>
                <AlertDialogDescription>
                  Are you sure you want to log out?
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={() => {
                    logout()
                    navigate({ to: "/login" })
                  }}
                >
                  Log out
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </header>

      {/* Main content area - fills remaining height */}
      <main className="flex-1 overflow-auto min-h-0">
        <Outlet />
      </main>

      <Footer
        footerLinks={(documentGroups ?? [])
          .flatMap((g) => g.documents)
          .filter((d) => d.document_type === "static_page" && d.show_in_footer)
          .map((d) => ({ title: d.title, slug: d.slug }))}
      />
    </div>
  )
}
